import { Box, IconNames } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { FullPresenceT, UserPresence } from 'lib-shared-legacy-types';
import type { Channel } from 'lib-shared-types-channels';
import { PropsWithChildren } from 'lib-shared-types-utils';
import partition from 'lodash/partition';
import { Fragment, ReactNode } from 'react';

import { WorkloadLimits } from 'apis/presence';
import { channelAttributes, mediaToChannels } from 'utils/channels';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';

type Workloads = UserPresence['workloads_by_channel'];

const [phoneChannels, voiceMailChannels] = partition<Readonly<Channel>>(
  mediaToChannels['speak'],
  (channel) => channel !== 'voicemail',
);
const emailChannels: readonly Channel[] = mediaToChannels['email'];
const chatChannels: readonly Channel[] = mediaToChannels['interactivechat'];
const messagingChannels: readonly Channel[] = mediaToChannels['messaging'];

const getWorkloadByChannel = (
  presence: FullPresenceT,
  channel: Channel,
): number => {
  const workload = presence.presence.presence_status.load_descriptions.filter(
    (load) =>
      load.conversation?.initial_channel ===
      convertChannelCasing(channel, 'truncate'),
  );
  return workload.length || 0;
};

export const getNumberOfConversations = (
  presence: FullPresenceT,
  channels: Channel[],
): number => {
  return channels
    .map((channel) => getWorkloadByChannel(presence, channel))
    .reduce((res, channelWorkload) => res + channelWorkload, 0);
};

const getChannelGroups = () => {
  const featureFlags = getChannelFeatureFlagsStatus();
  const filterByFeatureFlag = (channel) => {
    return featureFlags.get(channel);
  };
  return {
    phoneChannels: phoneChannels.filter(filterByFeatureFlag),
    voiceMailChannels: voiceMailChannels.filter(filterByFeatureFlag),
    emailChannels: emailChannels.filter(filterByFeatureFlag),
    chatChannels: chatChannels.filter(filterByFeatureFlag),
    messagingChannels: messagingChannels.filter(filterByFeatureFlag),
  };
};
export const getConversationGroups = (
  workloads: Workloads,
  limits: WorkloadLimits,
  fPresence: FullPresenceT | undefined,
): Array<{
  id: string;
  icon: IconNames;
  conversationCount: number;
  active: boolean;
  channels: Channel[];
  limit: number;
  tooltipContent: ReactNode;
}> => {
  if (!fPresence) return [];

  const {
    phoneChannels,
    voiceMailChannels,
    emailChannels,
    chatChannels,
    messagingChannels,
  } = getChannelGroups();

  const phoneCount = getNumberOfConversations(fPresence, phoneChannels);
  const voicemailCount = getNumberOfConversations(fPresence, voiceMailChannels);
  const emailCount = getNumberOfConversations(fPresence, emailChannels);
  const chatCount = getNumberOfConversations(fPresence, chatChannels);
  const messagingCount = getNumberOfConversations(fPresence, messagingChannels);

  return [
    {
      id: 'phone',
      icon: 'phone',
      conversationCount: phoneCount,
      active: isActiveOnChannelsGroup(fPresence, phoneChannels),
      channels: phoneChannels,
      limit: 1,
      tooltipContent: (
        <TooltipContent
          conversationCount={phoneCount}
          conversationLimit={1}
          text={'conversations'}
          channelBreakdowns={getChannelBreakdowns(fPresence, phoneChannels)}
        >
          <PhoneChannelBreakdown
            presence={fPresence}
            workloads={workloads}
            conversationCount={phoneCount}
          />
        </TooltipContent>
      ),
    },
    {
      id: 'voicemail',
      icon: 'voicemail',
      conversationCount: voicemailCount,
      active: isActiveOnChannelsGroup(fPresence, voiceMailChannels),
      channels: voiceMailChannels,
      limit: limits.voicemail,
      tooltipContent: (
        <TooltipContent
          conversationCount={voicemailCount}
          conversationLimit={limits['voicemail']}
          text={'voicemails'}
          channelBreakdowns={getChannelBreakdowns(fPresence, voiceMailChannels)}
        />
      ),
    },
    {
      id: 'email',
      icon: 'mail',
      conversationCount: emailCount,
      active: isActiveOnChannelsGroup(fPresence, emailChannels),
      channels: emailChannels,
      limit: limits['email'],
      tooltipContent: (
        <TooltipContent
          conversationCount={emailCount}
          conversationLimit={limits['email']}
          text={'conversations'}
          channelBreakdowns={getChannelBreakdowns(fPresence, emailChannels)}
        />
      ),
    },
    {
      id: 'chat',
      icon: 'chat',
      conversationCount: chatCount,
      active: isActiveOnChannelsGroup(fPresence, chatChannels),
      channels: chatChannels,
      limit: limits['chat'],
      tooltipContent: (
        <TooltipContent
          conversationCount={chatCount}
          conversationLimit={limits['chat']}
          text={'chats'}
          channelBreakdowns={getChannelBreakdowns(fPresence, chatChannels)}
        />
      ),
    },
    {
      id: 'messaging',
      icon: 'messaging-settings',
      conversationCount: messagingCount,
      active: isActiveOnChannelsGroup(fPresence, messagingChannels),
      channels: messagingChannels,
      limit: limits['messaging'],
      tooltipContent: (
        <TooltipContent
          conversationCount={messagingCount}
          conversationLimit={limits['messaging']}
          text={'conversations'}
          channelBreakdowns={getChannelBreakdowns(fPresence, messagingChannels)}
        />
      ),
    },
  ];
};

type TooltipContentProps = {
  conversationCount: number;
  conversationLimit: number;
  text: string;
  channelBreakdowns?: string[] | null;
} & PropsWithChildren;

export const TooltipContent = ({
  conversationCount: count,
  conversationLimit: limit,
  text,
  channelBreakdowns = null,
  children,
}: TooltipContentProps): JSX.Element => {
  if (channelBreakdowns && channelBreakdowns.length) {
    return (
      <>
        {`${count} / ${limit} ${text}`}
        <Box as="hr" margin="100 0" />
        {channelBreakdowns.map((channelBreakdown, i) => (
          <Fragment key={i}>
            {channelBreakdown}
            <br />
          </Fragment>
        ))}
        {children}
      </>
    );
  }

  return <>{`${count} / ${limit} ${text}`}</>;
};

type PhoneChannelBreakdownProps = {
  presence: FullPresenceT;
  workloads: Workloads;
  conversationCount: number;
};

const PhoneChannelBreakdown = ({
  presence,
  workloads,
  conversationCount,
}: PhoneChannelBreakdownProps): JSX.Element | null => {
  if (!conversationCount || !presence) return null;

  const loadDescriptions = presence.presence.presence_status.load_descriptions;
  const filteredLoadDescriptions = loadDescriptions.filter(
    (ld) => ld.conversation,
  );

  const externalPhoneConversations =
    (workloads['externalwork']?.length || 0) +
    (workloads['pstnphone']?.length || 0);

  const nonLivePhoneConversations = filteredLoadDescriptions.filter(
    (o) => o.load_type === 'nonlive' && o.conversation_channel === 'pstnphone',
  ).length;
  const internalPhoneConversations = filteredLoadDescriptions.filter(
    (o) =>
      o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone',
  ).length;

  return (
    <>
      <Box as="hr" margin="100 0" />
      {externalPhoneConversations} external
      <br /> {nonLivePhoneConversations} abandoned
      <br /> {internalPhoneConversations} internal
    </>
  );
};

const isActiveOnChannelsGroup = (
  presence: FullPresenceT,
  channels: Channel[],
): boolean => {
  const { presence: userPresence } = presence;
  const { working_channels: workingChannels } = userPresence;
  const active = workingChannels.some(
    (channel) =>
      channel.conversation_channel &&
      channels.includes(
        convertChannelCasing(channel.conversation_channel, 'snake') as Channel,
      ) &&
      channel.working,
  );
  return active;
};

const getChannelBreakdowns = (
  presence: FullPresenceT,
  channels: Channel[],
): string[] => {
  if (channels.length < 2) return [];

  const specificWorkloads: Array<[Channel, number]> = channels.map(
    (channel) => {
      const count = getWorkloadByChannel(presence, channel);
      return [channel, count];
    },
  );

  const hasSomeWorkload = specificWorkloads.some(([, count]) => count > 0);
  if (!hasSomeWorkload) return [];

  return specificWorkloads.map(([channel, count]) => {
    return `${count} ${channelAttributes[channel].name}`;
  });
};
